import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  api,
  type DuplicateAsin,
  type DuplicateKind,
  type DuplicatesResult,
} from '../api/client.js';
import { AnalysisBanner } from '../components/AnalysisBanner.js';
import { SkuStatus } from '../components/SkuStatus.js';
import {
  HistoryReading,
  KIND_LABEL,
  KindBadge,
  RemovalReading,
  VerdictBadge,
} from '../components/DuplicateBadges.js';
import { ProductThumb } from '../components/ProductThumb.js';
import { Alert, Card, PageHeader, Spinner, StockNote, TableScroll } from '../components/ui.js';
import { day, integer } from '../format.js';
import { familyPath } from '../lib/families.js';
import { productPagePath } from '../lib/products.js';

/**
 * Duplicate product entries: child ASINs that carry a duplicate seller SKU,
 * split into two sets — an ASIN with two or more Active SKUs, and an ASIN with
 * one Active SKU plus one or more non-Active (stale) twins. A product picks up a
 * second SKU when it moves from a manufacturer to a seller barcode; the stale
 * SKU usually keeps the old history with no stock. Each SKU shows the plan's own
 * hide verdict (from the shared rule), so the owner can see whether the plan
 * currently hides it. Read-only — the owner removes the stale ones in Seller
 * Central. See docs/analysis.md, "Duplicate seller SKUs".
 */

type Filter = 'all' | DuplicateKind;

export function DuplicatesPage() {
  const query = useQuery({
    queryKey: ['duplicates'],
    queryFn: () => api.get<DuplicatesResult>('/api/duplicates'),
  });

  const [filter, setFilter] = useState<Filter>('all');

  const groups = query.data?.groups ?? [];
  const counts = useMemo(() => {
    let multipleActive = 0;
    let activePlusInactive = 0;
    for (const g of groups) {
      if (g.kind === 'multiple_active') multipleActive += 1;
      else activePlusInactive += 1;
    }
    return { multipleActive, activePlusInactive, all: groups.length };
  }, [groups]);

  const shown = filter === 'all' ? groups : groups.filter((g) => g.kind === filter);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Duplicate product entries"
        description="Child ASINs carrying a duplicate seller SKU: two or more Active SKUs, or one Active SKU plus a stale non-Active twin. A product picks up a second SKU when it moves from a manufacturer to a seller barcode; the stale SKU usually keeps the old history with no stock. Each SKU shows whether the purchase plan hides it. Remove the stale ones in Seller Central."
      />

      <AnalysisBanner />

      {query.isPending && <Spinner label="Finding duplicates" />}
      {query.isError && <Alert>{query.error.message}</Alert>}

      {query.data && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted">
            <FilterTabs filter={filter} counts={counts} onChange={setFilter} />
            <StockNote source={query.data.stock.source} asOf={query.data.stock.asOf} />
          </div>

          {shown.length === 0 ? (
            <Card className="p-6">
              <p className="text-sm text-muted">
                {counts.all === 0
                  ? 'No child ASIN has a duplicate seller SKU. Nothing to clean up.'
                  : 'No product matches this filter.'}
              </p>
            </Card>
          ) : (
            <>
              <p className="text-xs text-muted">
                <span className="font-medium text-body">History captured</span> means a SKU's last
                sale falls inside the imported order coverage ({day(query.data.coverage.order.from)}
                –{day(query.data.coverage.order.to)}) and ledger coverage (
                {day(query.data.coverage.ledger.from)}–{day(query.data.coverage.ledger.to)}), so a
                stale twin can be removed in Seller Central without losing imported sales.
              </p>
              {shown.map((group) => (
                <DuplicateCard key={group.asin} group={group} />
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

/** The set filter, each option carrying its count so both sets are always visible. */
function FilterTabs({
  filter,
  counts,
  onChange,
}: {
  filter: Filter;
  counts: { all: number; multipleActive: number; activePlusInactive: number };
  onChange: (f: Filter) => void;
}) {
  const options: { value: Filter; label: string; count: number }[] = [
    { value: 'all', label: 'All', count: counts.all },
    { value: 'multiple_active', label: KIND_LABEL.multiple_active, count: counts.multipleActive },
    {
      value: 'active_plus_inactive',
      label: KIND_LABEL.active_plus_inactive,
      count: counts.activePlusInactive,
    },
  ];
  return (
    <div className="flex flex-wrap items-center gap-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`rounded px-2 py-1 text-xs font-medium ring-1 ring-inset ${
            filter === o.value
              ? 'bg-brand-50 text-brand-700 ring-brand-500'
              : 'text-muted ring-line hover:bg-sunken'
          }`}
        >
          {o.label} ({integer(o.count)})
        </button>
      ))}
    </div>
  );
}

/** One child ASIN and every seller SKU on it (all statuses), with the plan verdict. */
function DuplicateCard({ group }: { group: DuplicateAsin }) {
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2">
        <h2 className="inline-flex items-center gap-2 text-sm font-medium text-body">
          <ProductThumb asin={group.asin} title={group.productName} />
          {group.family ? (
            <Link to={familyPath(group.family.key)} className="text-brand-600 hover:underline">
              {group.productName}
            </Link>
          ) : (
            group.productName
          )}
          <span className="font-mono text-xs text-muted">{group.asin}</span>
          <Link
            to={productPagePath({ asin: group.asin, skuId: group.skus[0]?.id ?? 0 })}
            className="text-xs font-normal text-brand-600 hover:underline"
          >
            Open product
          </Link>
        </h2>
        <div className="flex items-center gap-2">
          <KindBadge kind={group.kind} />
          <span className="text-xs text-muted">
            {integer(group.activeSkuCount)} Active of {integer(group.skuCount)} SKU
            {group.skuCount === 1 ? '' : 's'}
          </span>
        </div>
      </div>
      <TableScroll>
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-xs text-muted">
            <tr>
              <th className="px-3 py-2 font-medium">Seller SKU</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 text-right font-medium">On hand</th>
              <th className="px-3 py-2 text-right font-medium">Inbound</th>
              <th className="px-3 py-2 text-right font-medium">Reserved</th>
              <th className="px-3 py-2 text-right font-medium">U365</th>
              <th className="px-3 py-2 text-right font-medium">Life</th>
              <th className="px-3 py-2 font-medium">Last sale</th>
              <th className="px-3 py-2 font-medium">First seen</th>
              <th className="px-3 py-2 font-medium">On the plan</th>
              <th className="px-3 py-2 font-medium">History</th>
              <th className="px-3 py-2 font-medium">Ready to remove</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {group.skus.map((s) => (
              <tr key={s.id}>
                <td className="whitespace-nowrap px-3 py-1.5">
                  <Link to={`/skus/${s.id}`} className="text-brand-600 hover:underline">
                    {s.sellerSku}
                  </Link>
                </td>
                <td className="whitespace-nowrap px-3 py-1.5 text-body">
                  <SkuStatus status={s.status} delistedOn={s.delistedOn} />
                </td>
                <td className="tabular px-3 py-1.5 text-right">{integer(s.onHand)}</td>
                <td className="tabular px-3 py-1.5 text-right">{integer(s.inbound)}</td>
                <td className="tabular px-3 py-1.5 text-right">{integer(s.reserved)}</td>
                <td className="tabular px-3 py-1.5 text-right">{integer(s.units365)}</td>
                <td className="tabular px-3 py-1.5 text-right">{integer(s.unitsLifetime)}</td>
                <td className="whitespace-nowrap px-3 py-1.5">{day(s.lastSaleDate)}</td>
                <td className="whitespace-nowrap px-3 py-1.5">{day(s.firstSeenDate)}</td>
                <td className="whitespace-nowrap px-3 py-1.5">
                  <VerdictBadge hidden={s.duplicateHidden} reason={s.hideReason} />
                </td>
                <td className="whitespace-nowrap px-3 py-1.5">
                  <HistoryReading captured={s.historyCaptured} reason={s.historyReason} />
                </td>
                <td className="whitespace-nowrap px-3 py-1.5">
                  <RemovalReading delistedOn={s.delistedOn} readyToRemove={s.readyToRemove} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </Card>
  );
}
